import React, { useState, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import axios from 'axios';
import '../custom.css';

const Roles = () => {
    const toast = useRef(null);
    const inputRef = useRef(null);

    const [roleName, setRoleName] = useState('');
    const [roles, setRoles] = useState([]);
    const [editingRole, setEditingRole] = useState(null);
    const [globalFilter, setGlobalFilter] = useState('');

    useEffect(() => {
        fetchRoles();
    }, []);

    const fetchRoles = async () => {
        try {
            const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/list/roles');
            setRoles(res.data);
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to fetch roles' });
        }
    };

    const handleSave = async () => {
        try {
            let res;
            if (editingRole) {
                res = await axios.post('https://treatway.co.in/Counter-Backend/api/server_masters/save/roles', { id: editingRole.roleId, name: roleName });
            } else {
                res = await axios.post('https://treatway.co.in/Counter-Backend/api/server_masters/save/roles', { name: roleName });
            }

            if (res.data.status === 'success') {
                toast.current.show({
                    severity: 'success',
                    summary: editingRole ? 'Updated' : 'Added',
                    detail: editingRole ? 'Role updated successfully' : 'Role added successfully'
                });
                setRoleName('');
                setEditingRole(null);
                fetchRoles();
            } else {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: res.data.message || 'Failed to save Role'
                });
                setTimeout(() => inputRef.current?.focus(), 0);
            }
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to save Role' });
        }
    };

    const confirmSave = () => {
        if (!roleName.trim()) {
            toast.current.show({ severity: 'warn', summary: 'Warning', detail: 'Role name is required' });
            return;
        }

        confirmDialog({
            message: editingRole ? 'Are you sure you want to update this role?' : 'Are you sure you want to add this role?',
            header: editingRole ? 'Confirm Update' : 'Confirm Add',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes',
            rejectLabel: 'No',
            acceptClassName: 'p-button-success',
            rejectClassName: 'p-button-secondary',
            accept: () => handleSave()
        });
    };

    const handleEdit = (rowData) => {
        setEditingRole(rowData);
        setRoleName(rowData.role);

        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, 0);
    };

    const confirmCancel = () => {
        confirmDialog({
            message: 'Are you sure you want to cancel editing?',
            header: 'Confirm Cancel',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes',
            rejectLabel: 'No',
            acceptClassName: 'p-button-danger',
            rejectClassName: 'p-button-secondary',
            accept: () => {
                setEditingRole(null);
                setRoleName('');
            }
        });
    };

    const actionBodyTemplate = (rowData) => {
        return <Button icon="pi pi-pencil" className="p-button-rounded p-button-warning mr-2" onClick={() => handleEdit(rowData)} />;
    };

    return (
        <>
            <ConfirmDialog />
            <Toast ref={toast} />
            <h2 className="text-xl font-bold mb-4">Role</h2>

            <div className="card">
                {/* Add / Edit Form */}
                <div className="flex flex-wrap align-items-end gap-3 mb-4">
                    <InputText ref={inputRef} value={roleName} onChange={(e) => setRoleName(e.target.value)} placeholder="Enter Role" style={{ width: '250px' }} />
                    <Button label={editingRole ? 'Update' : 'Save'} icon="pi pi-save" className="p-button-success" onClick={confirmSave} />
                    {editingRole && <Button label="Cancel" icon="pi pi-times" className="p-button-secondary" onClick={confirmCancel} />}
                </div>
            </div>

            <div className="card">
                <div className="flex flex-wrap justify-between items-center mb-4 gap-3">
                    <div className="flex items-center ml-auto min-w-[250px] justify-end w-full sm:w-auto">
                        <span className="p-input-icon-left w-full">
                            <i className="pi pi-search"></i>
                            <InputText value={globalFilter} onChange={(e) => setGlobalFilter(e.target.value)} placeholder="Search..." className="p-inputtext-sm w-full" />
                        </span>
                    </div>
                </div>

                <DataTable
                    value={roles}
                    showGridlines
                    paginator
                    rows={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                    currentPageReportTemplate="{first} to {last} of {totalRecords}"
                    tableStyle={{ minWidth: '50rem' }}
                    className="p-datatable-sm"
                    globalFilter={globalFilter}
                >
                    <Column field="roleId" header="ID" />
                    <Column field="role" header="Role" />
                    <Column header="Edit" body={actionBodyTemplate} />
                </DataTable>
            </div>
        </>
    );
};

export default Roles;
